/* eslint-disable no-console */
/**
 * Model Context Tool Registry Helper
 *
 * Provides safe registration and unregistration of tools
 * to prevent duplicate registration errors
 */

// Track registered tools to prevent duplicates
const registeredTools = new Set<string>();
const pendingTools = new Map<string, number>();
const abortControllers = new Map<string, AbortController>();

export interface ToolConfig {
  name: string;
  description: string;
  inputSchema: any;
  annotations?: any;
  execute: (args: any) => Promise<any>;
}

const getModelContext = () => {
  if (typeof document !== 'undefined' && document.modelContext) {
    return document.modelContext;
  }

  return typeof navigator !== 'undefined' ? navigator.modelContext : undefined;
};

const clearPendingRegistration = (name: string) => {
  const timer = pendingTools.get(name);
  if (timer) {
    window.clearInterval(timer);
    pendingTools.delete(name);
  }
};

/**
 * Safely register a tool with the model context
 * Prevents duplicate registration errors
 *
 * @param toolConfig - Tool configuration object
 * @returns boolean - true if successfully registered, false if already registered or error
 */
export const registerTool = (toolConfig: ToolConfig): boolean => {
  const { name } = toolConfig;

  if (registeredTools.has(name) || pendingTools.has(name)) return false;

  const register = () => {
    const modelContext = getModelContext();
    if (!modelContext) return false;

    try {
      const controller = new AbortController();
      abortControllers.set(name, controller);
      Promise.resolve(modelContext.registerTool(toolConfig, { signal: controller.signal }))
        .then(() => {
          registeredTools.add(name);
          clearPendingRegistration(name);
          console.log(`[ToolRegistry] Tool "${name}" registered successfully`);
        })
        .catch((error: Error) => {
          abortControllers.delete(name);
          clearPendingRegistration(name);
          console.error(`[ToolRegistry] Failed to register tool "${name}":`, error);
        });
      return true;
    } catch (error) {
      abortControllers.delete(name);
      console.error(`[ToolRegistry] Failed to register tool "${name}":`, error);
      return false;
    }
  };

  if (register()) return true;

  // Chrome extensions can inject WebMCP after React has mounted.
  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    if (register() || attempts >= 50) clearPendingRegistration(name);
  }, 100);
  pendingTools.set(name, timer);
  return true;
};

/**
 * Safely unregister a tool from the model context
 *
 * @param toolName - Name of the tool to unregister
 * @returns boolean - true if successfully unregistered, false otherwise
 */
export const unregisterTool = (toolName: string): boolean => {
  clearPendingRegistration(toolName);
  const controller = abortControllers.get(toolName);
  if (controller) {
    controller.abort();
    abortControllers.delete(toolName);
    registeredTools.delete(toolName);
    return true;
  }

  try {
    const modelContext = getModelContext();
    if (!modelContext?.unregisterTool) {
      return false;
    }

    modelContext.unregisterTool(toolName);

    // Remove from our tracker
    registeredTools.delete(toolName);
    console.log(`[ToolRegistry] ✅ Tool "${toolName}" unregistered successfully`);

    return true;
  } catch (error) {
    console.error(`[ToolRegistry] ❌ Failed to unregister tool "${toolName}":`, error);
    return false;
  }
};

/**
 * Check if a tool is registered
 *
 * @param toolName - Name of the tool to check
 * @returns boolean - true if registered, false otherwise
 */
export const isToolRegistered = (toolName: string): boolean => {
  return registeredTools.has(toolName);
};

/**
 * Get all registered tool names
 *
 * @returns Array of registered tool names
 */
export const getRegisteredTools = (): string[] => {
  return Array.from(registeredTools);
};

/**
 * Clear all tracked tools (useful for testing or reset)
 * Does not actually unregister from model context
 */
export const clearToolRegistry = (): void => {
  pendingTools.forEach((_, name) => clearPendingRegistration(name));
  abortControllers.forEach((controller) => controller.abort());
  abortControllers.clear();
  registeredTools.clear();
  console.log('[ToolRegistry] Registry cleared');
};
